import React, { useState } from 'react'
import { Button, Tag, Typography, Space, Modal, Divider, Card } from 'antd'
import { EditOutlined, UserOutlined, CarOutlined, CalendarOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { WorkOrder } from '@/types'
import FaseAccettazione from './FaseAccettazione'
import { usePermission } from '@/hooks/usePermission'

const { Text } = Typography

const PRIORITA_COLORS: Record<string, string> = {
  bassa: 'default',
  media: 'blue',
  alta: 'orange',
  urgente: 'red',
}

interface AccettazioneStripProps {
  workOrder: WorkOrder
  onSaved: (patch: Partial<WorkOrder>) => void
  /** Override dello style della Card. Vince sui margini di default.
   *  Usato da SchedaLavoroV3 per azzerare i margini laterali (layout
   *  a max-width già con padding, anziché a piena larghezza come V2). */
  style?: React.CSSProperties
}

const AccettazioneStrip: React.FC<AccettazioneStripProps> = ({ workOrder, onSaved, style }) => {
  const [editOpen, setEditOpen] = useState(false)
  const canEdit = usePermission('work_orders.edit')

  const vehicleLabel = [workOrder.vehicle_marca, workOrder.vehicle_modello].filter(Boolean).join(' ')
  const targa = workOrder.vehicle_targa ? ` (${workOrder.vehicle_targa})` : ''
  const appuntamento = workOrder.data_appuntamento
    ? dayjs(workOrder.data_appuntamento).format('DD/MM/YYYY HH:mm')
    : null
  const priorita = workOrder.priorita || 'media'
  const prioritaLabel = priorita.charAt(0).toUpperCase() + priorita.slice(1)

  // Sinistro details
  const sinistroDetails: string[] = []
  if (workOrder.sinistro) {
    if ((workOrder as any).insurance_branch?.nome) sinistroDetails.push((workOrder as any).insurance_branch.nome)
    if (workOrder.numero_sinistro) sinistroDetails.push(`N° ${workOrder.numero_sinistro}`)
    if (workOrder.compagnia_sinistro) sinistroDetails.push(workOrder.compagnia_sinistro)
    if (workOrder.compagnia_debitrice_sinistro) sinistroDetails.push(`Deb: ${workOrder.compagnia_debitrice_sinistro}`)
    if (workOrder.scoperto != null) sinistroDetails.push(`Scoperto ${workOrder.scoperto}%`)
    if (workOrder.franchigia != null) sinistroDetails.push(`Franchigia €${workOrder.franchigia}`)
    if (workOrder.legale) sinistroDetails.push(`Legale: ${workOrder.legale}`)
    if (workOrder.autorita) sinistroDetails.push(`Autorità: ${workOrder.autorita}`)
  }

  return (
    <>
      <Card
        size="small"
        style={{ margin: '12px 24px 0', ...style }}
        styles={{ body: { padding: '10px 16px' } }}
      >
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Riga 1: cliente · veicolo · appuntamento */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', alignItems: 'center', marginBottom: 4 }}>
            {workOrder.customer_nome ? (
              <Space size={4}>
                <UserOutlined style={{ color: '#1677ff', fontSize: 12 }} />
                <Text style={{ fontSize: 13, fontWeight: 500 }}>{workOrder.customer_nome}</Text>
              </Space>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>Nessun cliente</Text>
            )}
            {vehicleLabel ? (
              <Space size={4}>
                <CarOutlined style={{ color: '#595959', fontSize: 12 }} />
                <Text style={{ fontSize: 13 }}>{vehicleLabel}{targa}</Text>
              </Space>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>Nessun veicolo</Text>
            )}
            {appuntamento && (
              <Space size={4}>
                <CalendarOutlined style={{ color: '#595959', fontSize: 12 }} />
                <Text type="secondary" style={{ fontSize: 12 }}>{appuntamento}</Text>
              </Space>
            )}
            {workOrder.data_fine_prevista && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Cons: {dayjs(workOrder.data_fine_prevista).format('DD/MM/YYYY')}
              </Text>
            )}
          </div>

          {/* Riga 2: priorità · danno · sinistro + dettagli */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'center' }}>
            <Tag color={PRIORITA_COLORS[priorita]} style={{ margin: 0, fontSize: 11 }}>
              {prioritaLabel}
            </Tag>
            {workOrder.valutazione_danno ? (
              <Text
                type="secondary"
                style={{ fontSize: 12, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={workOrder.valutazione_danno}
              >
                {workOrder.valutazione_danno}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>Nessuna descrizione danno</Text>
            )}
            {workOrder.auto_cortesia_id && (
              <Tag color="green" icon={<CarOutlined />} style={{ margin: 0, fontSize: 11 }}>
                Auto cortesia
              </Tag>
            )}
            {workOrder.sinistro && (
              <Space size={4} wrap style={{ rowGap: 2 }}>
                <Tag color="gold" icon={<ExclamationCircleOutlined />} style={{ margin: 0, fontSize: 11 }}>
                  Sinistro
                </Tag>
                {sinistroDetails.map((detail, i) => (
                  <Text key={i} type="secondary" style={{ fontSize: 11 }}>
                    {i > 0 && <span style={{ marginRight: 4, color: '#d9d9d9' }}>·</span>}
                    {detail}
                  </Text>
                ))}
              </Space>
            )}
          </div>
        </div>

        {canEdit && (
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditOpen(true)}
            style={{ flexShrink: 0 }}
          >
            Modifica
          </Button>
        )}
      </div>
      </Card>

      <Modal
        title="Modifica Accettazione"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
        closable={false}
      >
        <Divider style={{ margin: '12px 0' }} />
        <FaseAccettazione
          workOrder={workOrder}
          isEditable={true}
          onSaved={(patch) => {
            onSaved(patch)
            setEditOpen(false)
          }}
          onCancel={() => setEditOpen(false)}
        />
      </Modal>
    </>
  )
}

export default AccettazioneStrip
